// Red de seguridad: un error de render no deja la plataforma en blanco y se reporta con la misma telemetría de la
// plataforma de siempre (activity_events, action='js_error', mismas llaves que components/ErrorBoundary:
// source='error_boundary' y component_stack).
//
//   modulo     etiqueta `module` del reporte. Como la de siempre: 'app_root' (todo el shell, App.js:742), 'app'
//              (cualquier página, App.js:578) y 'floating_chat' (la burbuja, App.js:676). La clave del módulo nuevo
//              va aparte en `pagina` (metadata.page_module) para no romper los tableros que filtran por module.
//   variante   'pagina' (default): tarjeta en el área de la página · 'compacto': aviso flotante chico (burbuja)
//              · 'linea': aviso dentro de una columna angosta (menú lateral) · 'pantalla': pantalla completa con su
//              propio lienzo (cuando falla el shell entero).
//   compacto   atajo de variante="compacto" (contrato anterior).
//
// Un chunk que ya no existe (pestaña abierta durante un deploy: los hashes viejos desaparecen) no se arregla
// reintentando el render (React.lazy guarda el rechazo): en ese caso el botón principal recarga la página.
import { Component } from 'react';
import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react';
import { Canvas, Glass } from '../../ds/components/core/Glass';
import { Button, IconButton } from '../../ds/components/core/Button';
import { reportClientError } from '../../utils/errorReporter';
import { useConfigUsuario } from '../app/ConfigUsuario';

const esChunkPerdido = (error) => error?.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(error?.message || '');

class Limite extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    reportClientError(this.props.username, this.props.modulo, error, {
      source: 'error_boundary',
      component_stack: (info?.componentStack || '').slice(0, 2000),
      ...(this.props.pagina ? { page_module: this.props.pagina } : {}),
    });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const { variante } = this.props;
    const recargar = () => window.location.reload();
    const chunk = esChunkPerdido(error);
    const reintentar = chunk ? recargar : () => this.setState({ error: null });

    if (variante === 'compacto') {
      return (
        <Glass strength="strong" padding="sm" role="alert" className="fixed bottom-4 right-4 z-overlay flex max-w-xs flex-col gap-2">
          <p className="text-body-sm font-semibold text-ink">Algo falló al mostrar esta sección</p>
          <Button variant="secondary" size="sm" icon={chunk ? RefreshCw : RotateCcw} onClick={reintentar}>
            {chunk ? 'Recargar página' : 'Reintentar'}
          </Button>
        </Glass>
      );
    }
    if (variante === 'linea') {
      return (
        <div role="alert" className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-body-sm text-ink-2">
          <span className="flex min-w-0 items-center gap-2">
            <AlertTriangle size={16} aria-hidden="true" className="shrink-0 text-warn" />
            <span className="min-w-0">No pudimos mostrar esta sección.</span>
          </span>
          <IconButton icon={chunk ? RefreshCw : RotateCcw} label={chunk ? 'Recargar página' : 'Reintentar'} size="sm" variant="ghost" onClick={reintentar} />
        </div>
      );
    }
    const tarjeta = (
      <Glass role="alert" className="flex flex-col items-center gap-3 py-12 text-center">
        <span className="db-relief grid h-12 w-12 place-items-center rounded-full text-warn">
          <AlertTriangle size={22} aria-hidden="true" />
        </span>
        <p className="font-display text-title-sm font-medium text-ink">
          {variante === 'pantalla' ? 'Algo salió mal al mostrar la plataforma' : 'Algo salió mal en este módulo'}
        </p>
        <p className="max-w-md text-body-sm text-ink-2">
          {chunk
            ? 'Hay una versión nueva de la plataforma. Recarga la página para continuar.'
            : variante === 'pantalla'
              ? 'Recarga la página para continuar. Ya registramos el detalle del error para revisarlo.'
              : 'El resto de la plataforma sigue funcionando. Ya registramos el detalle del error para revisarlo.'}
        </p>
        {error.message && !chunk && <p className="db-track max-w-md rounded-sm px-3 py-1.5 font-mono text-caption text-ink-3">{error.message}</p>}
        <div className="flex flex-wrap justify-center gap-2">
          {!chunk && <Button variant="secondary" icon={RotateCcw} onClick={reintentar}>Volver a intentar</Button>}
          <Button variant={chunk ? 'primary' : 'ghost'} icon={RefreshCw} onClick={recargar}>Recargar página</Button>
        </div>
      </Glass>
    );
    if (variante === 'pantalla') {
      return (
        <Canvas className="grid h-dvh place-items-center overflow-y-auto p-4">
          <div className="w-full max-w-lg">{tarjeta}</div>
        </Canvas>
      );
    }
    return <div className="p-4 md:p-6">{tarjeta}</div>;
  }
}

export function LimiteDeError({ modulo, pagina, variante, compacto = false, children }) {
  const { username } = useConfigUsuario();
  return (
    <Limite modulo={modulo} pagina={pagina} username={username} variante={variante || (compacto ? 'compacto' : 'pagina')}>
      {children}
    </Limite>
  );
}
